import { FC, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getTextFields } from "../variables/textFields";
import {
  Box,
  Button,
  Dialog,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CreatePayment from "../studentsPaymentEdit";
import useQuery from "../../../../../shared/components/hooks/useQuery";
import { ProfileBorderBox } from "../../../../../shared/components/profileComponents";
import { ViewContainer } from "../../../../../shared/components/viewContainer";
import SingleCardDetails from "../../../../../shared/components/singleCardDetails";
import { formatDateToDDMMYYYY } from "../../../../../shared/utils/dateFormat";
import { formatAmountINR } from "../../../../../shared/utils/amountFormat";
import { TitleBarProps } from "../../../../../shared/components/titleBar";

const StudentsView: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { loading, data, fetchData } = useQuery(`/admin/students/${id}`, null);

  const studentData = (data as any)?.data || {};
  const studentsEditFields = getTextFields(studentData);

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<{
    feeTermID: string;
    studentClassID: string;
    feeTermName: string;
    feeTermAmount: number;
  } | null>(null);

  // const closeDetailsView = () => {
  //   navigate("/students/student-list");
  // };

  const viewTitleBar: TitleBarProps = {
    title: "Student Details",
    editCloseIcon: {
      visible: true,
      onClick: () => navigate("/students/student-list"),
    },
  };

  const studentClass = studentData?.studentClass?.[0];
  const classInfo = studentClass?.class;
  const feeTerms = classInfo?.fee?.feeTerms || [];

  const feePaymentStatus = studentData?.feePaymentStatus || {};

  const unpaidTerms = feeTerms.filter(
    (term: any) => feePaymentStatus[term.name] === false
  );
  const paidTerms = feeTerms.filter(
    (term: any) => feePaymentStatus[term.name] === true
  );

  const handlePaymentClick = (term: any) => {
    setSelectedPaymentDetails({
      feeTermID: term.id,
      studentClassID: studentClass.id,
      feeTermName: term.name,
      feeTermAmount: term.amount,
    });
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    setSelectedPaymentDetails(null);
    fetchData();
    navigate(`/students/student-list/${id}`);
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
    setSelectedPaymentDetails(null);
  };

  return (
    <ProfileBorderBox>
      <ViewContainer loading={loading} titleBar={viewTitleBar}>
        <div className="entity-container">
          <SingleCardDetails
            entityFields={studentsEditFields}
            //   tooltips={<CloseButton onClick={closeDetailsView} />
            // }
          >
            {/* Pending Fee Payments Section */}
            <Box sx={{ mt: 0.25, p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Pending Fee Payments
              </Typography>

              {unpaidTerms.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {unpaidTerms.map((term: any) => (
                    <Box
                      key={term.id}
                      sx={{
                        p: 3,
                        borderRadius: "12px",
                        backgroundColor: "#F8F9FA",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        border: "1px solid #E9ECEF",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                          transition: "all 0.3s ease",
                        },
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#495057",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: "#FF6B6B",
                                display: "inline-block",
                              }}
                            />
                            {term.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#6C757D",
                              mt: 0.5,
                            }}
                          >
                            Amount: {formatAmountINR(term.amount)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#6C757D",
                              mt: 0.5,
                            }}
                          >
                            Due On: {formatDateToDDMMYYYY(term.dueOn)}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          color="primary"
                          size="medium"
                          sx={{
                            minWidth: "120px",
                            fontWeight: 500,
                            textTransform: "none",
                            boxShadow: "none",
                            "&:hover": {
                              backgroundColor: "secondary.dark",
                              boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                            },
                          }}
                          disabled={
                            (data as any)?.entityPermissions
                              ? !(
                                  (data as any)?.entityPermissions as string[]
                                )?.includes("Offline Payments-Create")
                              : false
                          }
                          onClick={() => handlePaymentClick(term)}
                        >
                          PAY {term.name}
                        </Button>
                      </Box>

                      {/* Table: Fee Split */}
                      <Typography variant="subtitle2" color="#495057">
                        Fee Breakdown
                      </Typography>
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ mt: -1 }}
                      >
                        <Table size="small">
                          <TableBody>
                            {[...Object.entries(term.split || {})]
                              .filter(([key]) => key !== "Grand Total")
                              .reverse()
                              .map(([key, value]) => (
                                <TableRow key={key}>
                                  <TableCell component="th" scope="row">
                                    {key}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatAmountINR(value as number)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            <TableRow>
                              <TableCell component="th" scope="row">
                                <strong>Grand Total</strong>
                              </TableCell>
                              <TableCell align="right">
                                <strong>{formatAmountINR(term.amount)}</strong>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body1" sx={{ color: "#6C757D" }}>
                  No pending fee payments.
                </Typography>
              )}
            </Box>

            {/* Paid Fee Payments Section */}
            <Box sx={{ mt: 0.25, p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Paid Fee Payments
              </Typography>

              {paidTerms.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {paidTerms.map((term: any) => (
                    <Box
                      key={term.id}
                      sx={{
                        p: 3,
                        borderRadius: "12px",
                        backgroundColor: "#F0FFF0",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        border: "1px solid #D4EDDA",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: "#28a745",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <CheckCircleOutlineIcon
                            sx={{ color: "#28a745", fontSize: 20 }}
                          />
                          {term.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#495057",
                            mt: 0.5,
                          }}
                        >
                          Amount: {formatAmountINR(term.amount)}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        color="success"
                        size="medium"
                        disabled
                        sx={{
                          minWidth: "120px",
                          fontWeight: 500,
                          textTransform: "none",
                          cursor: "not-allowed",
                        }}
                      >
                        Paid
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body1" sx={{ color: "#6C757D" }}>
                  No paid fee payments.
                </Typography>
              )}
            </Box>
          </SingleCardDetails>
        </div>
      </ViewContainer>

      {/* Payment Modal */}
      {showPaymentForm && selectedPaymentDetails && (
        <Dialog
          open={showPaymentForm}
          onClose={handlePaymentCancel}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "12px",
              padding: 2,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            },
          }}
        >
          <CreatePayment
            feeTermID={selectedPaymentDetails.feeTermID}
            studentClassID={selectedPaymentDetails.studentClassID}
            feeTermName={selectedPaymentDetails.feeTermName}
            feeTermAmount={selectedPaymentDetails.feeTermAmount}
            onSuccess={handlePaymentSuccess}
            onCancel={handlePaymentCancel}
          />
        </Dialog>
      )}
    </ProfileBorderBox>
  );
};

export default StudentsView;
